import { UNTAGGED_VALUE } from '~publish/components/FilterByTag'
import type { FilterablePostType } from '~publish/helpers/postType'
import type { PostStatus } from '~publish/components/graphqlEnums'
import type { DateTimePresence } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

type TagId = string
type ChannelId = string
export type PostFilterViolation =
  | 'channelIds'
  | 'status'
  | 'tagIds'
  | 'dueAtPresence'
  | 'postTypes'

type RelevantPostSubset = {
  channel?: { id: ChannelId }
  channelId?: ChannelId
  tags?: ({ id: TagId } | TagId)[]
  status: PostStatus
  dueAt?: string | null
  metadata?: { type?: string | null } | null
}
type PostFilters = {
  channelIds?: ChannelId[] | null
  status?: PostStatus[] | null
  tagIds?: TagId[] | null
  dueAtPresence?: DateTimePresence | null
  postTypes?: readonly FilterablePostType[] | null
}

/**
 * Whether a post's tags satisfy a tag filter selection.
 *
 * A post satisfies the filter when either the "untagged" option is selected and
 * the post has no tags, or the post carries at least one of the selected tags.
 * An empty selection means "no tag filter", so every post satisfies it.
 */
export const postSatisfiesTagFilter = (
  postTagIds: TagId[],
  tagFilterIds: TagId[],
): boolean => {
  if (tagFilterIds.length === 0) {
    return true
  }
  const hasUntaggedFilter = tagFilterIds.includes(UNTAGGED_VALUE)
  const otherTagIds = tagFilterIds.filter((id) => id !== UNTAGGED_VALUE)
  const postHasNoTags = postTagIds.length === 0
  const postHasMatchingTag = otherTagIds.some((tagId) =>
    postTagIds.includes(tagId),
  )
  return (hasUntaggedFilter && postHasNoTags) || postHasMatchingTag
}

/**
 * Whether a post's format satisfies a post type selection.
 *
 * `post` is the fallback bucket, so a post whose stored format is absent or is
 * one the filter cannot name still matches a `post` selection. That mirrors the
 * server's `$nin` predicate and the badge `metadata.type` renders, so a
 * real-time insert agrees with what the query would have returned.
 */
export const postSatisfiesPostTypeFilter = (
  postType: string | null | undefined,
  selected: readonly FilterablePostType[],
): boolean => {
  if (selected.length === 0) return true
  const format: FilterablePostType =
    postType === 'reel' || postType === 'story' ? postType : 'post'
  return selected.includes(format)
}

export const isPostSatisfyingFilters = (
  post: RelevantPostSubset,
  filters: PostFilters,
): { satisfies: boolean; filterViolations: PostFilterViolation[] } => {
  const violations: PostFilterViolation[] = []
  const postChannelId = post.channelId ?? post.channel?.id
  if (
    filters.channelIds &&
    filters.channelIds.length > 0 &&
    !filters.channelIds.includes(postChannelId ?? 'invalid')
  ) {
    violations.push('channelIds')
  }

  if (
    filters.status &&
    filters.status.length !== 0 &&
    !filters.status.includes(post.status)
  ) {
    violations.push('status')
  }

  const postTagIds =
    post.tags?.map((tag) => (typeof tag === 'string' ? tag : tag.id)) ?? []
  if (
    filters.tagIds &&
    filters.tagIds.length > 0 &&
    !postSatisfiesTagFilter(postTagIds, filters.tagIds)
  ) {
    violations.push('tagIds')
  }

  if (
    filters.postTypes &&
    filters.postTypes.length > 0 &&
    !postSatisfiesPostTypeFilter(post.metadata?.type, filters.postTypes)
  ) {
    violations.push('postTypes')
  }

  const hasDueAt = Boolean(post.dueAt)
  if (filters.dueAtPresence === 'present' && !hasDueAt) {
    violations.push('dueAtPresence')
  }
  if (filters.dueAtPresence === 'absent' && hasDueAt) {
    violations.push('dueAtPresence')
  }

  return {
    satisfies: violations.length === 0,
    filterViolations: violations,
  }
}
